import { z } from 'zod'

import { lenientList } from '@/schemas/common'

/**
 * The two company wire shapes the published OpenAPI cannot describe, written
 * by hand from mm-be's serializers (`api/v5/company/serializers.py`, origin/dev
 * 2508a1a) and PARSED at runtime — with no generated type behind them, the
 * parse is the only thing that makes these types true.
 *
 * Every other company read takes its wire type from `@/api/schema` through
 * `Wire<>`, as the rest of the app does. These two cannot:
 *
 *  - PANEL. `/panel/{tab}/` has no response schema and no query parameters in
 *    the OpenAPI: the `@extend_schema(responses=PanelSerializer, …)` meant for
 *    `CompanyPanelView` sits on `CompanyMetricsView` as a class decorator
 *    (views.py:211), where the method-level decorator overrides it. So the
 *    generated type says "no response body".
 *  - DIVIDEND SUMMARY. `/dividends/` puts its stat line in `meta.summary`, and
 *    every list's `meta` is free-form in the OpenAPI.
 *
 * Each `z.infer` type below is the wire type for its read, derived from the
 * schema rather than declared beside it, so the two cannot drift. Decimals
 * stay `z.string()` here; the app schemas in `@/schemas/company-profile`
 * brand them once the mappers have run.
 *
 * TODO(tech-debt): once mm-be moves the panel's `@extend_schema` onto
 * `CompanyPanelView`, regenerate and replace `PanelWire` with `Wire<'Panel'>`
 * (and the panel's hand-built query string in `profileService.ts` with typed
 * `params.query`).
 */

// ── /panel/{tab}/ — PanelSerializer ──────────────────────────────────────────

/** PanelSectorStatsSerializer. `verdict`: ABOVE | BELOW | ABOUT_THE_SAME. */
const panelSectorStatsWire = z.object({
  median: z.string(),
  low: z.string(),
  high: z.string(),
  members: z.number().int(),
  verdict: z.string(),
})

/** MetricReadingSerializer — every field independently nullable but `code`. */
const metricReadingWire = z.object({
  code: z.string(),
  favourable: z.string().nullable(),
  reading: z.string().nullable(),
  comparison: z.string().nullable(),
  judgement: z.string().nullable(),
  explainer: z.string().nullable(),
})

/** RegulatoryReferenceSerializer — the same fields `/reference/regulatory-limits/` serves. */
const regulatoryReferenceWire = z.object({
  code: z.string(),
  rule: z.string(),
  label: z.string(),
  value: z.string(),
  unit: z.string().nullable(),
  comparator: z.string(),
  sector: z.string().nullable(),
  word_within: z.string().nullable(),
  word_breached: z.string().nullable(),
  explainer: z.string().nullable(),
  source: z.string().nullable(),
  effective_from: z.string().nullable(),
})

/** LimitTestSerializer. */
const limitTestWire = z.object({
  limit: regulatoryReferenceWire,
  within: z.boolean().nullable(),
  headroom: z.string().nullable(),
  word: z.string().nullable(),
})

/** PanelColumnSerializer. */
const panelColumnWire = z.object({
  key: z.string(),
  label: z.string(),
  unit: z.string().nullable(),
  value: z.string().nullable(),
  sector: panelSectorStatsWire.nullable(),
  reading: metricReadingWire.nullable(),
  regulatory: limitTestWire.nullable(),
})

/**
 * PanelSerializer. `year` arrives as the upstream writes it (`082-083`) and
 * `period` as q1–q4 or `annual`; `availability` is AVAILABLE | MISSING_PERIOD
 * | NO_ELIGIBLE_PERIOD. A column or limit that fails to parse is dropped with
 * a warning rather than blanking the tab.
 */
export const panelWireSchema = z.object({
  symbol: z.string(),
  tab: z.string(),
  slug: z.string(),
  revision: z.number().int(),
  year: z.string().nullable(),
  period: z.string().nullable(),
  availability: z.string(),
  available_tabs: z.array(z.string()),
  columns: lenientList(panelColumnWire, 'panel column'),
  regulatory: lenientList(limitTestWire, 'panel limit'),
  is_sample: z.boolean(),
})

export type PanelWire = z.infer<typeof panelWireSchema>
export type PanelColumnWire = z.infer<typeof panelColumnWire>
export type MetricReadingWire = z.infer<typeof metricReadingWire>
export type LimitTestWire = z.infer<typeof limitTestWire>
export type RegulatoryReferenceWire = z.infer<typeof regulatoryReferenceWire>

// ── /dividends/ meta.summary — DividendSummarySerializer ─────────────────────

export const dividendSummaryWireSchema = z.object({
  next_book_closure_date: z.string().nullable(),
  days_to_book_closure: z.number().int().nullable(),
  window_years: z.number().int().nullable(),
  years_paid: z.number().int().nullable(),
  average_total_percent: z.string().nullable(),
})

export type DividendSummaryWire = z.infer<typeof dividendSummaryWireSchema>
